import type { Translations } from '../i18n';

const en: Translations = {
  // ── Nav ──────────────────────────────────────────────────────────────────────
  'nav.map': 'Map',
  'nav.myRoutes': 'My routes',
  'nav.collections': 'Collections',
  'nav.stats': 'Stats',
  'nav.method': 'Method',
  'nav.login': 'Log in',
  'nav.account': 'My account',
  'nav.ariaLabel': 'Main navigation',
  'nav.menu': 'Menu',
  'nav.myStats': 'See my stats',
  'nav.myTraces': 'My traces',
  'nav.contribute': 'Import / Contribute',
  'nav.menuAriaLabel': 'Navigation menu',
  'nav.privacy': 'Privacy',
  'nav.support': 'Support',
  'nav.overlay': 'Export the per-sport heatmap (overlay)',

  // ── Members-only community EXPORT gate (2026-07 posture) ───────────────────
  // The community MAP is public (anyone can view the heatmap); only the bulk
  // ODbL community-data EXPORT is members-only — it's the conversion point.
  'exportGate.title': 'Log in to export the community data',
  'exportGate.subtitle': 'The ODbL community-heatmap export is members-only — log in to download it, and contribute your own traces to help the map grow.',
  'exportGate.cta': 'Log in to export the ODbL community data',
  'exportGate.sub': "Email only, no password — we send you a login link. Once you're in, you can export the community layer and drop your own traces.",

  // ── Community heatmap EXPORT modal (formats, bbox, async, ODbL) ────────────
  'export.title': 'Export the community layer (heatmap)',
  'export.intro': 'Download the community layer (every trace shared by contributors). Data published under the ODbL 1.0 licence — attribution and share-alike required. Use in a closed product without share-alike: a separate commercial licence is available, contact us.',
  'export.calque.title': '🗺️ The PER-SPORT heatmap as an overlay (gpx.studio / VisuGPX)',
  'export.calque.bodyBefore': 'Show ONE sport\'s heatmap (road, gravel, MTB, running…) underneath and draw on top — URL below is Road, other sports in the guide. In gpx.studio or VisuGPX, add a ',
  'export.calque.bodyStrong': 'custom map layer',
  'export.calque.bodyAfter': ' (raster / XYZ type) with this tiles URL:',
  'export.calque.copy': 'Copy',
  'export.calque.copied': '✓ Copied',
  'export.calque.tutorialCta': 'Use the heatmap as an overlay (step-by-step + per sport)',
  'export.downloads.legend': 'Downloads',
  'export.pmtiles.desc': '— vector tiles, all of France, MapLibre / pmtiles.js',
  'export.geojsonl.desc': '— newline-delimited GeoJSON (gzip), all of France, gpx.studio / QGIS',
  'export.odbl.before': 'I accept the ',
  'export.odbl.after': ' licence (attribution “© CHEMINS COMMUNS contributors” required, share-alike).',
  'export.cancel': 'Cancel',
  'export.download': 'Download',

  'support.breadcrumb': 'Support & Help',
  'support.title': 'Support & Help',
  'support.intro': 'Common Trails is an open-source project run by a small team. We answer every message personally — here is how to reach us.',
  'support.contactTitle': 'Contact us',
  'support.contactText': 'A question, feedback, or a request about your data or account? Email us — we usually reply within a few days.',
  'support.dataTitle': 'Your data',
  'support.dataText': 'Your tracks and activities belong to you. You can delete them at any time from your account, and revoke access to a connected third-party account (Strava, Garmin) on their platform.',
  'support.dataLink': 'Read the privacy policy',
  'support.backToMap': '← Back to map',
  'strava.viewOn': 'View on Strava',

  // ── Mobile ──────────────────────────────────────────────────────────────────

  // ── Common ───────────────────────────────────────────────────────────────────
  'common.close': 'Close',
  'common.cancel': 'Cancel',
  'common.delete': 'Delete',
  'common.save': 'Save',
  'common.back': '← Back',
  'common.or': 'or',
  'common.create': 'Create',
  'common.retry': 'Retry',
  'common.loading': 'Loading…',
  'common.noName': 'Unnamed',
  'common.all': 'All',
  'common.restore': 'Restore',
  'common.details': 'Details',
  'common.clear': 'Clear',
  'common.appName': 'Common Trails',

  // ── Route actions (ViewedRoutePanel) ────────────────────────────────────────
  'route.share': 'Share',
  'route.edit': 'Edit',
  'route.fork': 'Save as variant',
  'route.forkSuccess': 'Variant created ✓',
  'route.viewOriginal': 'View original',
  'route.variantsBy_one': '{count} cyclist adapted this route',
  'route.variantsBy_other': '{count} cyclists adapted this route',
  'route.variantsHint': 'Toggle a variant to overlay it on the map',

  // ── Sports ───────────────────────────────────────────────────────────────────
  'sport.road': 'Road',
  'sport.gravel': 'Gravel',
  'sport.mtb': 'MTB',
  'sport.offroad': 'Off-road',
  'sport.running': 'Running / Trail',
  'sport.running_short': 'Running',
  'sport.running_desc': 'Footpaths, trails',

  // ── Surfaces ─────────────────────────────────────────────────────────────────
  'surface.asphalt': 'asphalt',
  'surface.gravel': 'gravel',
  'surface.dirt': 'dirt',
  'surface.rock': 'rock',
  'surface.unknown': 'unknown',
  'surface.mixed': 'mixed',

  // ── POI types ────────────────────────────────────────────────────────────────
  'poi.water': 'Water',
  'poi.food': 'Food',
  'poi.camp': 'Campsite',
  'poi.shelter': 'Shelter',
  'poi.shop': 'Shop',
  'poi.train': 'Train station',
  'poi.viewpoint': 'Viewpoint',
  'poi.custom': 'Other',

  // ── Server wakeup banner ─────────────────────────────────────────────────────
  'wakeup.ready': 'Server ready!',
  'wakeup.starting': 'Starting server…',
  'wakeup.startingLabel': 'Starting — {label}…',

  // ── Layout ───────────────────────────────────────────────────────────────────
  'layout.beta': 'Beta — send your bugs with as much detail and screenshots as possible to',
  'layout.description': 'Plan your cycling, gravel and MTB routes guided by community GPS traces. Open source, open data (ODbL).',
  'layout.ogAlt': 'Chemins Communs — community cycling trace map',

  // ── Home page ────────────────────────────────────────────────────────────────
  // Tagline = the value prop, visible on EVERY viewport (the subtitle is hidden
  // on mobile and short viewports) — it must carry the whole pitch on its own.
  'home.hero.tagline': 'The community cycling heatmap: the more a trail is ridden, the brighter it glows. Everything you need to build real routes.',
  // {thesis} = common.thesis — the project's motto, quoted here and on /methode
  // from ONE key so the two pages can't drift apart (they already had: the old
  // methode key said "travel them" while the hero said "ride them").
  'home.hero.subtitle': '“{thesis}” Road, gravel, MTB — an open, free map built from the traces you share, under the ODbL licence.',
  'common.thesis': 'The trails belong to those who ride them.',
  'home.hero.legendLow': 'lightly ridden',
  'home.hero.legendHigh': 'heavily ridden',
  'home.stats.contributors': 'contributors',
  'home.stats.traces': 'traces',
  'home.stats.km': 'km of paths',
  'home.community.title': "What's a heatmap, and why does it matter?",
  'home.community.text': "A heatmap colours paths by how many cyclists actually ride them. The darker, the more popular: at a glance you see the routes locals prefer, and the ones they avoid.\n\nStrava popularised the idea but locked it behind a paywall. Here it's open and collective: every trace you import enriches the map for everyone, under the ODbL licence. Your start and end points are masked to protect your home address.",
  'home.elevation.title': 'Elevation profile & surfaces',
  'home.elevation.desc': 'Copernicus DEM altitude, slope-coloured profile, surface estimation with confidence index. D+ and D- on every trace.',
  'home.trails.desc': 'Forest defence tracks (DFCI) and marked trails (GR, GRP, GT, PR, EuroVelo) shown on the map.',
  'home.openSource.text': "Your GPS shouldn't enrich closed platforms. Here, every shared trace stays a common good — open data ODbL, source code AGPLv3.",
  // Hero overlay + section headings (previously hardcoded FR literals → caused
  // the mixed-language hero: t()'d strings followed the locale, these did not)
  // Placement-neutral label (footer + logged-in hero); the "no account"
  // acquisition variant is hero-only, for visitors without an account.
  'home.hero.exploreMap': 'Explore the heatmap →',
  'home.hero.exploreMapNoAccount': 'Explore the heatmap — no account needed →',
  'home.hero.contribute': 'Contribute to the map',
  'home.hero.connected': 'Connected',
  'home.hero.logout': 'Log out',
  'home.hero.join': 'Add your traces to the map 🚴',
  'home.hero.joinSub': 'Create your account in one email, then import your Strava, Garmin or GPX export.',
  'home.onboarding.ctaTitle': 'Ready to put your paths on the map?',
  'home.onboarding.cta': 'Create my account and import my traces →',
  'home.onboarding.ctaSub': 'Free · Strava, Garmin or GPX · open ODbL data',
  'home.hero.disconnected': 'You have been logged out.',
  'home.badge.anonymized': 'Endpoints masked',
  'home.badge.openSource': 'Open source',
  'home.section.howItWorks': 'How it works',
  'home.section.howItWorksSub': 'From the community heatmap to your own traces, in three steps.',
  'home.step1.title': 'Enjoy a heatmap',
  'home.step1.desc': 'Explore the paths the community actually rides — the real routes, not the lines drawn by an algorithm.',
  'home.step2.title': 'Export your data',
  // {strava} / {garmin} are placeholders → rendered as real links in app/page.tsx.
  'home.step2.desc': 'From {strava}, {garmin} or your own GPX files — every shared trace enriches the common map, under the ODbL licence.',
  'home.step2.strava': 'Strava',
  'home.step2.garmin': 'Garmin',
  'home.step3.title': 'Build real routes',
  'home.step3.desc': "Never built a route before? Follow the brightest trails: the heatmap gives you a clean GPX, ready for gpx.studio, VisuGPX or your GPS device.",
  "home.calque.title": "🗺️ Use the per-sport heatmap as an overlay",
  "home.calque.intro": "Show the heatmap underneath in gpx.studio and draw your route on top. 30 seconds: Settings → Map Layers → Custom layers, and paste this URL:",
  "home.calque.copy": "Copy",
  "home.calque.copied": "✓ Copied",
  "home.calque.tutorialCta": "Step-by-step guide + per-sport overlay",
  'home.trails.title': 'DFCI fire tracks · Marked trails GR / GRP / GT / PR / EuroVelo',
  'home.philosophy.title': 'Take back control of our traces',
  'home.philosophy.method': 'Methodology',
  'home.footer.license': 'ODbL 1.0 · Endpoints masked · AGPLv3',

  // ── Home — Auth ──────────────────────────────────────────────────────────────
  'home.auth.login': 'Log in',
  'home.auth.register': 'Register',
  'home.auth.username': 'Username',
  'home.auth.email': 'Email',
  'home.auth.password': 'Password',
  'home.auth.loginBtn': 'Log in',
  'home.auth.registerBtn': 'Create my account',
  'home.auth.stravaLogin': 'Log in with Strava',

  // ── Passwordless email login (magic link) ────────────────────────────────────
  'emailAuth.title': 'Create an account or log in',
  'emailAuth.subtitle': "Enter your email — we'll send you a login link.",
  'emailAuth.emailPlaceholder': 'you@example.com',
  'emailAuth.sendBtn': 'Send my link',
  'emailAuth.sending': 'Sending…',
  'emailAuth.sentTitle': 'Check your inbox ✉️',
  'emailAuth.sentBody': 'If an account exists for that address, a login link has just been sent. The link is valid for 15 minutes.',
  'emailAuth.resend': 'Send another link',
  'emailAuth.error': 'Sending failed. Please try again in a moment.',
  'emailAuth.verifying': 'Signing you in…',
  'emailAuth.verifySuccess': 'Signed in! Redirecting…',
  'emailAuth.verifyError': 'This login link is invalid or has expired.',
  'emailAuth.verifyMissingToken': 'Incomplete login link.',
  'emailAuth.backHome': 'Back to home',

  // Account unification — add/confirm a real email on a Strava-only account.
  'accountEmail.bannerTitle': 'Add your email',
  'accountEmail.bannerBody': 'Attach an email address to your account so you can log back in without Strava and receive your notifications (a new ride on the map, etc.).',
  'accountEmail.placeholder': 'you@example.com',
  'accountEmail.submit': 'Add my email',
  'accountEmail.sending': 'Sending…',
  'accountEmail.sentTitle': 'Check your inbox ✉️',
  'accountEmail.sentBody': "A confirmation link was just sent to that address. Click it to finalize — the address is only attached once confirmed.",
  'accountEmail.conflict': 'That email is already used by another account. Log in with the email login instead.',
  'accountEmail.error': 'Sending failed. Please try again in a moment.',
  'accountEmail.confirming': 'Confirming…',
  'accountEmail.confirmSuccess': 'Email confirmed! Redirecting…',
  'accountEmail.confirmError': 'This confirmation link is invalid or has expired.',
  'accountEmail.confirmMissingToken': 'Incomplete confirmation link.',
  'accountEmail.backToStrava': 'Back to Strava',

  // ── Map page ─────────────────────────────────────────────────────────────────
  'map.basemap.cyclOSM': 'CyclOSM — elevation & bike infrastructure',
  'map.elevCalc': 'Computing optimal elevation…',
  'map.gravelCheck': 'Checking if it works for gravel…',
  'map.importSuccess': '✓ {count} activity(ies) imported',
  'map.gpxDownloaded': '✓ GPX downloaded',
  'map.importFailed': '{count} failed',
  'map.betaBanner': 'Beta — every imported GPS trace enriches the community map.',
  'map.highContrast': 'High contrast',
  'map.sidebarStats': 'Distance, elevation, records…',
  'map.activitiesCount': '{count} imported activity(ies)',
  'map.importTitle': 'Import / Contribute',
  'map.import.launcherBody': 'One single place to contribute your traces to the common map: drop your GPX, FIT or a Strava / Garmin / Komoot archive, with a single ODbL consent.',
  'map.import.launcherCta': 'Import / Contribute my traces',
  'map.close': 'Close',
  'map.euroVeloTitle': 'EuroVelo · European cycling routes',
  'map.layerEuroVelo': 'EuroVelo',
  'map.layerMtbTrails': 'Marked MTB',
  'map.layerEuroVeloMtb': 'EuroVelo / Marked MTB',
  'map.traceImported': '✓ Trace imported! Heatmap updating...',

  // ── Map — Routing explanations ───────────────────────────────────────────────
  'map.elevLoading': 'Loading elevation…',
  'map.elevUnavailable': 'Altitude data unavailable — estimated profile',
  'map.surfaceMethodology': 'Methodology: percentages are computed on known distance only (OSM surface tags). Segments without reliable data are reported separately.',
  'map.surfaceDisclaimer': '% computed on known distance · hover for methodology',

  // ── Map — Garmin import panel ────────────────────────────────────────────────

  // ── Map — How routing works panel ────────────────────────────────────────────
  'map.howRouting.title': 'How is the route calculated?',
  'map.howRouting.intro': "We don't look for the shortest route. We look for a coherent route — one you'd actually want to ride.",
  'map.howRouting.frequency': 'Real traffic',
  'map.howRouting.frequencyDesc': 'paths that other cyclists already ride',
  'map.howRouting.surface': 'Surface type',
  'map.howRouting.surfaceDesc': 'adapted to your bike (road, gravel, MTB…)',
  'map.howRouting.elevation': 'Elevation and slope',
  'map.howRouting.elevationDesc': 'estimated effort based on chosen profile',
  'map.howRouting.trails': 'Marked trails',
  'map.howRouting.trailsDesc': "GR, PR, MTB routes… when it's relevant",
  'map.howRouting.detour': 'Detour limitation',
  'map.howRouting.detourDesc': "a popular path is never forced if it creates an incoherent detour",

  // ── Map — Annotation presets ─────────────────────────────────────────────────
  'annotation.water': 'Water point',
  'annotation.food': 'Food stop',
  'annotation.viewpoint': 'Viewpoint',
  'annotation.danger': 'Danger',
  'annotation.info': 'Info',
  'annotation.photo': 'Photo',
  'annotation.shelter': 'Shelter',
  'annotation.repair': 'Repair',

  // ── Discover page ────────────────────────────────────────────────────────────
  'discover.hotZone': 'The community rides here — areas to discover',
  'discover.cloneSuccess': '✓ Variant created — open it to edit',
  'discover.noRoutes': 'No shared route for this sport',
  'discover.beFirst': 'Be the first to share a route!',
  'discover.beFirstDesc': 'Plan a route on the map, save it as public, and it will appear here.',
  'discover.createFirst': '🗺️ Create the first route',
  'discover.discoverRoutes': '🗺️ Discover routes',
  'discover.sortByPlace': '📍 Sorted by proximity to {placeName} · {profile}',
  'discover.sortByActivity': '📍 Sorted by proximity to your activity · {profile}',
  'discover.subtitle': 'Routes shared by the community · clone, propose variants',
  'discover.allSports': 'all sports',
  'discover.unexplored.title': 'Unexplored zones',
  'discover.unexplored.subtitle': 'Not yet covered',
  'discover.unexplored.cta': 'Explore →',
  'discover.unexplored.fallback': 'Unexplored zone',
  'discover.loginToCreate': 'Log in to create a variant.',
  'discover.distMin': 'Min dist. (km)',
  'discover.distMax': 'Max dist. (km)',
  'discover.sortNewest': 'Newest',
  'discover.sortPopular': 'Most popular',
  'discover.compareCancel': 'Cancel',
  'discover.compareEnable': 'Compare',
  'discover.selectedCount': '{count} routes',
  'discover.refresh': 'Refresh',
  'discover.visibleArea': 'in the visible area',
  'discover.popularity': 'Popularity',

  // ── Stats page ───────────────────────────────────────────────────────────────
  'stats.loginRequired': 'Log in to see your stats.',
  'stats.totalDistance': 'Total distance',
  'stats.exploredZones': 'Explored zones',
  'stats.sportsPracticed': 'Sports',
  'stats.personalRecords': 'Your personal records',
  'stats.activitiesForTotal': 'activities for a total of',
  'stats.elevGain': 'Elevation gain',
  'stats.bestYear': 'was your best year with',
  'stats.bestMonth': 'was your best month with',
  'stats.biggestClimb': '⛰️ Biggest climb',
  'stats.tracesLoading': 'Loading my traces…',
  'stats.importedTracesTitle': 'My imported traces',
  'stats.noImportedTraces': 'You haven’t imported any traces yet.',
  'stats.noImportedTracesSub': 'Upload your Strava archive or GPX files to find your rides here.',
  'stats.importCta': 'Import my traces',
  'stats.noTraceMatch': 'No trace found.',
  'stats.deleteTrace': 'Delete this trace',
  'stats.deleteTraceConfirm': 'Delete "{name}"? This trace will be removed from your statistics and its contribution to the community heatmap will be deleted. This action is irreversible.',
  'stats.deleteTraceError': 'Deletion failed. Please try again.',
  'stats.heatmapContrib': 'Community data (ODbL) — {contributors} contributor{s}, {edges} GPS segments',

  // ── Strava page ──────────────────────────────────────────────────────────────
  'strava.pageHeader': 'Contribute to the common map',
  'strava.pageSubtitle': 'Drop your traces — GPX, FIT or a .zip archive (Strava or Garmin) — they join the common map (ODbL, endpoints masked).',
  'strava.pageSubtitleStravaLink': 'your Strava export',
  'strava.flow.step1': 'Get your traces',
  'strava.flow.step2': 'Drop them here',
  'strava.flow.step3': 'They join the map (you get an email)',
  'strava.contribStatus.done': "You've already contributed {count} trace(s) to the common map 🙌 Add your latest rides below.",
  'strava.contribStatus.none': "You haven't contributed to the heatmap yet — drop your files below to join the common map.",
  'strava.signupToContribute': 'Create your account to contribute',
  'strava.signupToContributeSub': 'Free, one email — your consent to share is tied to your account.',
  'strava.depositTitle': 'Drop my traces',
  'strava.depositSub': 'GPX, FIT or ZIP — multiple files accepted.',
  'strava.stravaHelpSummary': 'Using Strava? Request your archive in 3 minutes',
  'strava.garminHelpSummary': 'Using Garmin? Export your data in 2 clicks',
  'strava.garminHelpBody': 'Go to Garmin Connect › Account Data › Export Your Data, download the .zip archive and drop it here. Tip: dropping your individual GPX files (Activities) is often faster than one big export.',
  'strava.garminHelpLink': 'Export my Garmin data →',
  'strava.connected': 'Strava connected!',
  'strava.preparingImport': 'Preparing the import...',
  'strava.welcome': 'Welcome{name}!',
  'strava.estimatedActivities': '{n} activity(ies) on your account',
  'strava.loginToImport': 'Log in to import.',
  'strava.dataImported': 'GPS traces, names, distances, elevation',
  'strava.dataPrivate': 'Private data — visible only to you',
  'strava.heatmapContrib': 'Aggregated heatmap (your traces are never individually visible) — ODbL licence',
  'strava.estimatedTime': 'Estimated time: ~{minutes} min',
  'strava.oneByOne': 'Each GPS trace is downloaded from Strava one by one.',
  'strava.bulkExportHint': 'For an instant, high-resolution import:',
  'strava.requestDataHint': '(click "Request your data" — this does not delete your account).',
  'strava.activitiesFound': 'activity(ies) found',
  'strava.fetchingList': 'Fetching the activities list...',
  'strava.alreadyPresent': 'already present',
  'strava.gpsUpgrade': 'High-resolution GPS upgrade',
  'strava.apiLimit': 'Strava import is limited by their API (~400 activities/hour).',
  'strava.requestData': 'Strava → Settings → Request your data',
  'strava.importGpxHint': "(Import GPX button). It's instant and high-resolution.",
  'strava.rateLimitNote': 'Limited by Strava API (~400 activities/hour)',

  // ── Strava — import personal archive (consented ODbL contribution) ─────────
  // NOTE: consent wording is DRAFT — Paul to finalize the legal copy.
  'strava.archive.title': 'Import my Strava export',
  'strava.archive.intro': "Upload the archive of YOUR Strava data (the one Strava emails you on request): your traces join the open community map (ODbL), your start & end points masked.",
  'strava.archive.whyTitle': 'Why upload a file instead of just connecting Strava?',
  'strava.archive.whyBody': "Strava can show you your rides, but can't pour them into an open map. Your data belongs to you: by uploading your export yourself, you share it — your start & end points masked — in a free map no one can lock away. That's the whole spirit of Common Trails. 🚲",
  'strava.archive.reassure': "This is Strava's official export of YOUR data (your right to your data), NOT account deletion. Nothing is deleted.",
  'strava.archive.howto': 'How to get your archive:',
  'strava.archive.s1': 'Strava → Settings → "My Account"',
  'strava.archive.s2': 'Under {exportLink} → "Request Your Archive" button → "Get Started"',
  'strava.archive.s2LinkLabel': '"Download or Delete Your Account"',
  'strava.archive.s3': 'Strava emails you a download link (this can take several days).',
  'strava.archive.s4': 'Download the .zip file, then drop it below.',
  'strava.archive.helpLink': 'How to — official Strava guide',
  'strava.archive.exportPageLink': 'Open my Strava data export page',
  'strava.archive.dropOnHero': 'Once the ZIP lands in your inbox, just drop it in the zone above.',
  // ⚠️ LEGAL WORDING (consent v5) — any material change here must bump
  // CONTRIBUTION_CONSENT_VERSION (lib/strava-archive-consent.ts). The second
  // sentence is the dual-licensing grant.
  'strava.archive.consentLabel': 'I consent to contributing my traces to the open community popularity map, published under the ODbL 1.0 licence. I grant Chemins Communs a non-exclusive right to also offer them under a commercial licence (revenue funds the project; my traces stay free under ODbL). The start and end of each activity are masked to protect my sensitive locations (home, work).',
  'strava.archive.consentRequired': 'Tick the consent box to continue.',
  'strava.archive.consentHint': 'Tick the box to enable uploading.',
  'strava.archive.preparing': 'Preparing the archive…',
  'strava.archive.uploadingPct': 'Uploading your archive… {pct}%',
  'strava.archive.finalizing': 'Finalising your upload…',
  'strava.archive.acceptedQueued': 'Your archive was received — your traces will be added to the map progressively.',
  'strava.archive.failed': 'Archive upload failed.',
  'strava.archive.importAnother': 'Upload another archive',
  // Logged-out gate: the export how-to + wizard preview are shown to everyone,
  // but the actual deposit needs an account (consent is tied to a user).

  // ── Strava — fun comments ────────────────────────────────────────────────────
  'strava.fun.zigzag': 'That zigzag ride looks suspicious...',
  'strava.fun.bigClimb': 'Ouch, those calves must be burning!',
  'strava.fun.impressiveElev': 'Wow, impressive elevation!',
  'strava.fun.earlyMorning': 'A 5am ride? Respect!',
  'strava.fun.nicePass': 'Nice mountain pass, respect.',
  'strava.fun.sundayClub': 'Smells like a Sunday club ride.',
  'strava.fun.gpsGlitch': 'GPS glitch or secret shortcut?',
  'strava.fun.forest': 'A lovely forest ride! No hunters I hope?',
  'strava.fun.rain': 'Another ride in the rain, huh?',
  'strava.fun.niceLoop': 'Nice loop, no shortcuts.',
  'strava.fun.timeTrialLike': 'Looks like a time trial!',
  'strava.fun.almostDone': 'Almost there, just a few more...',
  'strava.fun.pedaling': 'Pedalling hard around here.',
  'strava.fun.noFlat': 'Does a day without hills even exist?',
  'strava.fun.headwind': 'That one must have had serious headwind!',
  'strava.fun.gravelAvg': 'Nice average speed for gravel!',
  'strava.fun.noFlat2': 'No flat tyre on that one?',
  'strava.fun.novemberRain': 'Rainy November ride, classic.',
  'strava.fun.bakeryStop': 'The classic bakery stop.',
  'strava.fun.recognizeArea': 'I recognise this area.',

  // ── Me / Stats ───────────────────────────────────────────────────────────────
  'meStats.loginToDownload': 'Log in to download your data.',
  'meStats.downloaded': '✓ Data downloaded',
  'meStats.downloadBtn': '⬇ Download my data',
  'meStats.noActivities': 'No imported activity for this profile.',

  // ── Me / Routes ──────────────────────────────────────────────────────────────
  'meRoutes.title': 'My routes',
  'meRoutes.loginRequired': 'Log in to see your routes.',
  'meRoutes.loadError': 'Unable to load routes: {error}',
  'meRoutes.search': 'Search...',
  'meRoutes.sortLastAccessed': 'Last accessed',
  'meRoutes.sortCreatedAt': 'Date created',
  'meRoutes.sortDistance': 'Distance',
  'meRoutes.noRoutes': 'No routes found.',
  'meRoutes.createOnMap': 'Create a route on the map',
  'meRoutes.drafts': 'Drafts ({count})',
  'meRoutes.routes': 'Routes ({count})',
  'meRoutes.trash': 'Recycle bin ({count})',
  'meRoutes.groups': 'Groups',
  'meRoutes.routeCount': '{count} route{s}',
  'meRoutes.compareOnMap': 'Compare on map',
  'meRoutes.deleteGroup': 'Delete',
  'meRoutes.deleteGroupConfirm': 'Delete this group? Routes will not be deleted.',
  'meRoutes.newGroup': 'New group...',
  'meRoutes.createGroup': '+ Create',
  'meRoutes.yourRoutes': 'Your routes',
  'meRoutes.deleteConfirm': 'Delete this route? It will be recoverable for 30 days.',

  // ── Me / Stats (extra) ─────────────────────────────────────────────────────
  'meStats.loginRequired': 'Log in to see your statistics.',
  'meStats.loadError': 'Unable to load stats: {error}',
  'meStats.loading': 'Loading statistics…',
  'meStats.period30d': '30 days',
  'meStats.period1y': '12 months',
  'meStats.periodAll': 'All',
  'meStats.activities': 'Activities',
  'meStats.totalDistance': 'Total distance',
  'meStats.totalElev': 'Total D+',
  'meStats.visitedCells': 'Visited cells',
  'meStats.tiles': 'tiles',
  'meStats.personalCoverage': 'Personal coverage',
  'meStats.exploredTiles': 'You have explored {count} distinct tiles in {profile} mode.',
  'meStats.last30d': 'last 30 days',
  'meStats.last12m': 'last 12 months',
  'meStats.coverageComingSoon': 'Coverage details (tile map, top cells) will be available in a future version.',
  'meStats.importTraces': 'Import your first traces',

  // ── Routes page ─────────────────────────────────────────────────────────────
  'routes.redirecting': 'Redirecting…',

  // ── Activities (extra) ──────────────────────────────────────────────────────
  'activities.loading': 'Loading…',
  'activities.missingId': 'Missing ID',
  'activities.loginRequired': 'Log in to see your traces.',
  'activities.notFound': 'Trace not found.',
  'activities.backHome': 'Back to home',
  'activities.distance': 'Distance',
  'activities.time': 'Time',
  'activities.avgSpeed': 'Avg speed',
  'activities.date': 'Date',
  'activities.noElevProfile': 'Elevation profile unavailable — re-import from Strava to get point-by-point data.',
  'activities.viewOnMap': 'View on map',
  'activities.similarRoutes': 'Similar routes →',
  'activities.linkCopied': 'Link copied!',
  'activities.share': 'Share',
  'activities.mapBreadcrumb': 'Map',
  'activities.detailBreadcrumb': 'Trace detail',

  // ── Collections (extra) ─────────────────────────────────────────────────────
  'collections.newCollection': 'New collection',
  'collections.nameLabel': 'Collection name',
  'collections.descLabel': 'Description (optional)',
  'collections.cancel': 'Cancel',
  'collections.myCollections': 'My collections',
  'collections.public': 'Public',
  'collections.loadingCount': 'Loading...',
  'collections.count': '{count} collection{s}',
  'collections.new': '+ New',
  'collections.emptyTitle': 'No collections yet',
  'collections.emptyDesc': 'Group your routes into collections!',
  'collections.createFirst': '+ Create a collection',
  'collections.loading': 'Loading...',
  'collections.backToCollections': 'Back to collections',
  'collections.noStagesYet': 'No routes yet',
  'collections.addRoute': '+ Add a route',
  'collections.publicTab': 'Public',
  'collections.myTracesTab': 'My traces',
  'collections.searchRoute': 'Search for a route...',
  'collections.searching': 'Searching...',
  'collections.noResults': 'No results',
  'collections.viewOnMap': 'View on map',
  'collections.duplicate': 'Duplicate',
  'collections.shareBtn': 'Share',
  'collections.copied': 'Copied!',
  'collections.traces': 'Traces',
  'collections.addDescription': '+ Add a description',
  'collections.save': 'Save',
  'collections.variant': 'Variant',
  'collections.private': 'Private',
  'collections.planned': 'Planned',
  'collections.draft': 'Draft',
  'collections.detailBreadcrumb': 'Detail',
  'collections.stageDesc': 'Description for this trace...',
  'collections.collectionDesc': 'Collection description...',
  'collections.removeStageConfirm': 'Remove this trace from the collection?',
  'collections.poiClickHint': 'Hover over the trace and click to place a point',
  'collections.poiAdd': 'Add',
  'collections.poiCancel': 'Cancel',
  'collections.poiNameLabel': 'Name (optional)',
  'collections.mapBtn': 'Map',

  // ── Admin (extra) ───────────────────────────────────────────────────────────
  'admin.title': 'Admin Dashboard',
  'admin.monitoring': 'CHEMINS COMMUNS — monitoring',
  'admin.updatedAt': 'updated {time}',
  'admin.refresh': 'Refresh',
  'admin.error': 'Error: {error}',
  'admin.notLoggedIn': 'Not logged in — log in first',
  'admin.errorStatus': 'Error {status}',
  'admin.resyncNow': 'Resync now',
  'admin.launching': 'Launching…',
  'admin.tokenExpired': 'Token expired',
  'admin.never': 'Never',

  // ── Admin dashboard — cards / stats / columns (routed from hardcoded FR, hardening) ──
  'admin.apiRevision': 'Current API revision:',
  'admin.card.users': 'Users',
  'admin.card.activities': 'Activities',
  'admin.card.totalDistance': 'Total distance',
  'admin.card.engagement': 'Engagement',
  'admin.card.provenance': 'Activity sources',
  'admin.card.bySport': 'Activities by sport',
  'admin.card.routes': 'Routes',
  'admin.card.heatmap': 'Community heatmap',
  'admin.card.graphHealth': 'Routing graph health',
  'admin.card.network': 'Trail network',
  'admin.card.database': 'Database',
  'admin.card.recentRoutes': 'Recent routes',
  'admin.card.recentActivities': 'Recent activities',
  'admin.card.recentJobs': 'Recent job runs',
  'admin.card.stravaSync': 'Strava sync',
  'admin.stat.signedUp': 'signed up',
  'admin.stat.importedTraces': 'imported traces',
  'admin.stat.cumulativeDistance': 'cumulative',
  'admin.stat.cumulativeElevation': 'cumulative',
  'admin.stat.signups7d': 'signups 7d',
  'admin.stat.signups30d': 'signups 30d',
  'admin.stat.active7d': 'active 7d',
  'admin.stat.active30d': 'active 30d',
  'admin.stat.stravaConnected': 'Strava connected',
  'admin.stat.total': 'total',
  'admin.stat.edges': 'edges',
  'admin.stat.cells': 'cells',
  'admin.stat.maxContributors': 'max contributors',
  'admin.stat.pctOnOsm': '% on OSM',
  'admin.stat.pctGridFallback': '% grid-fallback',
  'admin.stat.vertices': 'vertices',
  'admin.stat.deadEnds': 'dead-ends',
  'admin.stat.pctDeadEnds': '% dead-ends',
  'admin.stat.dfciTracks': 'DFCI tracks',
  'admin.stat.markedTrails': 'marked trails',
  'admin.stat.size': 'size',
  'admin.stat.connected': 'connected',
  'admin.stat.delay30d': 'delay > 30d',
  'admin.stat.tokenExpired': 'token expired',
  'admin.provider.gpxUpload': 'GPX upload',
  'admin.col.name': 'Name',
  'admin.col.author': 'Author',
  'admin.col.sport': 'Sport',
  'admin.col.visibility': 'Visibility',
  'admin.col.distance': 'Distance',
  'admin.col.elevation': 'D+',
  'admin.col.created': 'Created',
  'admin.col.source': 'Source',
  'admin.col.imported': 'Imported',
  'admin.col.job': 'Job',
  'admin.col.execution': 'Execution',
  'admin.col.status': 'Status',
  'admin.col.start': 'Start',
  'admin.col.duration': 'Duration',
  'admin.col.athlete': 'Athlete',
  'admin.col.lastSync': 'Last sync',
  'admin.col.delay': 'Delay',
  'admin.col.failures': 'Failures',
  'admin.sport.road': 'Road',
  'admin.sport.gravel': 'Gravel',
  'admin.sport.mtb': 'MTB',
  'admin.sport.offroad': 'Off-road',
  'admin.sport.running': 'Running',

  // ── Privacy (extra) ─────────────────────────────────────────────────────────
  'privacy.title': 'Privacy Policy',
  'privacy.whoWeAre': '1. Who we are',
  'privacy.whoWeAreText': 'CHEMINS COMMUNS — Chemins Communs is an open source (AGPLv3) community cycling mapping platform. Source code available on',
  'privacy.responsible': 'Contact: Paul Leclercq — paul@epauler.fr',
  'privacy.dataCollectedIntro': 'When you connect a third-party account (Strava, Garmin Connect) or import a GPX file, we collect:',
  'privacy.gpsTraces': 'Your GPS traces (coordinates, altitude, timestamps)',
  'privacy.thirdPartyId': 'Third-party account identifier (for synchronisation)',
  'privacy.alsoCollect': 'We also collect:',
  'privacy.email': 'Email address (for authentication)',
  'privacy.useTitle': '4. Data usage',
  'privacy.displayTraces': 'Display your traces on the map (visible only to you)',
  'privacy.feedHeatmap': 'Feed the open community popularity map (voluntarily uploaded traces, endpoints masked)',
  'privacy.personalStats': 'Display your personal statistics',
  'privacy.weDontTitle': 'We do not:',
  'privacy.noAds': 'No advertising, no ad tracking',
  'privacy.noSale': 'No resale or sharing of data with third parties',
  'privacy.noProfiling': 'No behavioural profiling',
  'privacy.thirdPartyTitle': '5. Third-party integrations (Strava, Garmin Connect)',
  'privacy.retentionTitle': '7. Retention and deletion',
  'privacy.youCan': 'You can at any time:',
  'privacy.deleteActivity': 'Delete an individual activity from the Statistics page ("My traces" section)',
  'privacy.heatmapRecalc': 'Deleting an activity immediately removes its contribution from the community heatmap data; the rendered map background is updated at the next regeneration.',
  'privacy.cookiesTitle': '8. Cookies',
  'privacy.cookiesText': 'We only use an authentication cookie (JWT) necessary for the service. No advertising cookie, no third-party cookie, no analytics tracking.',
  'privacy.gdprTitle': '9. Your rights (GDPR)',
  'privacy.rightRectification': 'Rectification: correct inaccurate data',
  'privacy.rightDeletion': 'Deletion: request erasure of your data',
  'privacy.rightOpposition': 'Opposition: withdraw your consent at any time',
  'privacy.exerciseRights': 'To exercise these rights: paul@epauler.fr',
  'privacy.changesTitle': '10. Changes',
  'privacy.backToMap': '← Back to map',
  'privacy.hostingDb': 'Database: Cloud SQL PostgreSQL, encryption at rest',
  'privacy.hostingTls': 'Communications: mandatory HTTPS (TLS 1.2+)',
  'privacy.hostingAuth': 'Authentication: JWT with expiration, bcrypt password hashing',
  'privacy.breadcrumb': 'Privacy Policy',

  // ── RouteCard ────────────────────────────────────────────────────────────────
  'routeCard.draft': 'Draft',
  'routeCard.forkedFrom': 'Inspired by an original',
  'routeCard.forkCount': '{count} variant{s}',
  'routeCard.openMap': '🗺️ Open map',
  'routeCard.makePrivate': '🔒 Make private',
  'routeCard.makePublic': '🌍 Make public',
  'routeCard.clone': '🍴 Variant',

  // ── Strava Import Banner ─────────────────────────────────────────────────────
  'importBanner.strava': 'Strava import in progress...',
  'importBanner.gpsUpgrade': 'GPS HD: {upgraded}/{total}',
  'importBanner.importing': 'Import: {imported}/{total}',
  'importBanner.photos': 'Importing photos...',

  // ── PlaceSearch ──────────────────────────────────────────────────────────────
  'placeSearch.placeholder': 'Search for a place...',

  // ── PublicRoutesPanel ────────────────────────────────────────────────────────
  'publicRoutes.title': '🗺️ Public routes',
  'publicRoutes.count': '{count} visible',
  'publicRoutes.noRoutes': 'No route in this area',

  // ── ErrorBoundary ────────────────────────────────────────────────────────────
  'error.title': 'An error occurred',
  'error.unknown': 'Unknown error',

  // ── ElevationProfile ─────────────────────────────────────────────────────────
  'elevation.title': 'Elevation profile',

  // ── Route PRs ────────────────────────────────────────────────────────────────
  'pr.statusOpen': 'Open',
  'pr.statusAccepted': 'Accepted',
  'pr.statusRejected': 'Rejected',
  'pr.title': '🔀 Proposals ({count})',
  'pr.none': 'No proposals',
  'pr.accept': 'Accept',
  'pr.reject': 'Reject',
  'pr.proposeToOriginal': 'Propose to original{name}',
  'pr.titleLabel': 'Proposal title',
  'pr.descLabel': 'Description (optional)',
  'pr.createError': 'Error creating proposal',
  'pr.networkError': 'Network error',
  'pr.createBtn': 'Create proposal',

  // ── Route Tags ───────────────────────────────────────────────────────────────
  'tag.title': '🏷 Milestones ({count})',
  'tag.none': 'No milestones',
  'tag.add': '+ Milestone',
  'tag.nameLabel': 'Milestone name (e.g. v1.0)',
  'tag.messageLabel': 'Message (optional)',
  'tag.createError': 'Error creating tag',
  'tag.networkError': 'Network error',

  // ── Collections ──────────────────────────────────────────────────────────────
  'collections.creating': 'Creating...',
  'collections.createBtn': 'Create collection',
  'collections.subtitle': 'Group routes by theme, region or trip project.',
  'collections.example.traversees': 'Grand Traversals',
  'collections.example.traverseesDesc': 'GT MTB, EuroVelo, GR — the marked classics',
  'collections.example.region': 'By region',
  'collections.example.regionDesc': 'Lozère, Luberon, Grands Causses...',
  'collections.example.multiday': 'Multi-day trips',
  'collections.example.multidayDesc': 'Plan your stages, lodging, points of interest',
  'collections.example.compare': 'Compare variants',
  'collections.example.compareDesc': 'Same destination, different routes',
  'collections.done': 'Done',
  'collections.cancelPoi': '✕ Cancel',
  'collections.addPoi': '📍 Add a point of interest',
  'collections.poiTitle': 'Points of interest',
  'collections.deletePoi': 'Delete this point of interest?',
  'collections.poiHint': 'Use the "📍 Add a point of interest" button on the map to place points (water, food, campsite...)',
  'collections.day': 'Day {n}',
  'collections.restDay': 'Rest',
  'collections.addRestDay': '+ Rest day',
  'collections.tripStats': 'Overview',
  'collections.totalDays': '{n} day{s}',
  'collections.avgPerDay': 'avg. {km}/day',
  'collections.longestDay': 'longest',
  'collections.shortestDay': 'shortest',
  'collections.lodgingCamping': 'Camping',
  'collections.lodgingHotel': 'Hotel',
  'collections.lodgingRefuge': 'Refuge',
  'collections.lodgingFree': 'Free',
  'collections.lodgingNone': 'None',
  'collections.lodging': 'Lodging',
  'collections.estimatedTime': 'Estimated time',
  'collections.timeMin': '{n} min',
  'collections.timeH': '{h}h{m}',
  'collections.assignDay': 'Day',
  'collections.newDay': 'New day',
  'collections.unassociatedPois': 'General POIs',

  // ── Activities ───────────────────────────────────────────────────────────────
  'activities.elevGain': 'Elevation +',
  'activities.routeCreated': '✓ Route created →',
  'activities.creating': 'Creating…',
  'activities.createRoute': '🔀 Create a route',

  // ── Admin ────────────────────────────────────────────────────────────────────
  'admin.restricted': 'Admin access only',
  'admin.totalElev': 'Total elevation',
  'admin.disabled': 'Disabled',
  'admin.disabledPlural': 'disabled',
  // ── Admin — users table (observability) ───────────────────────────────
  'admin.users.section': 'Users (detail)',
  'admin.users.empty': 'No users.',
  'admin.users.count': '{n} user(s)',
  'admin.users.col.user': 'User',
  'admin.users.col.role': 'Role',
  'admin.users.col.joined': 'Joined',
  'admin.users.col.strava': 'Strava',
  'admin.users.col.sync': 'Sync',
  'admin.users.col.traces': 'Traces',
  'admin.users.col.distance': 'Distance',
  'admin.users.col.sports': 'Sports',
  'admin.users.col.lastActivity': 'Last activity',
  'admin.users.role.admin': 'Admin',
  'admin.users.role.user': 'Member',
  'admin.users.strava.none': 'Not linked',
  'admin.users.strava.viewProfile': 'View Strava profile',
  'admin.users.sync.ok': 'OK',
  'admin.users.sync.reconnect': 'Reconnect required',
  'admin.users.sync.tokenExpired': 'Token expired',
  'admin.users.sync.failures': '{n} failure(s)',
  'admin.users.sync.never': 'Never synced',
  'admin.users.skipped': '{n} skipped',

  // ── Admin: heatmap monitoring (freshness + evolution) ─────────────────────────
  'admin.mon.freshness': 'Freshness & health',
  'admin.mon.evolution': 'Heatmap evolution',
  'admin.mon.lastActivity': 'Last activity ingested',
  'admin.mon.lastRebuild': 'Last heatmap rebuild',
  'admin.mon.lastResync': 'Last resync',
  'admin.mon.stravaSync': 'Strava sync',
  'admin.mon.webhook': 'Strava webhook subscription',
  'admin.mon.rel.never': 'never',
  'admin.mon.rel.now': 'just now',
  'admin.mon.rel.min': '{n} min ago',
  'admin.mon.rel.h': '{n} h ago',
  'admin.mon.rel.d': '{n} d ago',
  'admin.mon.resync.completed': 'OK',
  'admin.mon.resync.running': 'running',
  'admin.mon.resync.pending': 'pending',
  'admin.mon.resync.failed': 'failed',
  'admin.mon.resync.none': 'none',
  'admin.mon.strava.reconnect': 'reconnect required',
  'admin.mon.strava.failures': '{n} sync failure(s)',
  'admin.mon.strava.ok': 'OK',
  'admin.mon.strava.none': 'no account connected',
  'admin.mon.webhook.present': 'present',
  'admin.mon.webhook.missing': 'missing',
  'admin.mon.ways': '{n} aggregated ways',
  'admin.mon.series.heatEdges': 'Segments (heat_edges)',
  'admin.mon.series.activities': 'Activities',
  'admin.mon.series.networkKm': 'Network (km)',
  'admin.mon.empty': 'No history yet — the first point appears after the next rebuild or the daily cron.',
  'admin.mon.snapshots': '{n} snapshot(s)',
  'admin.mon.latest': 'current: {value}',

  // ── Admin: archives (contributions) — the pending_archives queue ─────────────
  'admin.archives.title': 'Archives (contributions)',
  'admin.archives.empty': 'No archives uploaded.',
  'admin.archives.status.awaiting_upload': 'awaiting upload',
  'admin.archives.status.uploaded': 'uploaded',
  'admin.archives.status.processing': 'processing',
  'admin.archives.status.done': 'done',
  'admin.archives.status.failed': 'failed',
  'admin.archives.col.user': 'User',
  'admin.archives.col.status': 'Status',
  'admin.archives.col.progress': 'Imported / skipped / failed',
  'admin.archives.col.error': 'Last error',
  'admin.archives.col.updated': 'Updated',
  'admin.archives.attempts': '{n} attempt(s)',
  'admin.archives.members': 'of {n} files',
  'admin.archives.requeue': 'Requeue',

  // ── Méthode page ─────────────────────────────────────────────────────────────
  'methode.heroTitle': "How does it work?",
  'methode.heroSubtitle': "Real trails, not lines on a map.",
  'methode.principleTitle': "The principle",
  'methode.rawIntro': "The community map shows your real GPS traces, precise, exactly as you rode them. No algorithm redraws them onto a road network. The more a path is ridden, the more it stands out: popular routes emerge from thousands of overlapping traces.",
  'methode.rawNoMatch': "No map-matching, no snapping to a known network. What you see is the raw aggregate of real rides — desire lines included: the singletracks and forest tracks no map knows about.",
  'methode.pill.masking': "Endpoints masked",
  'methode.pill.commercial': "Commercial licence on request",
  'methode.rawBuildTitle': "Raw traces, overlaid",
  'methode.rawBuildDesc': "The recipe is simple: we read your GPX file, mask its start and end, then overlay it. Popularity is counted on a fine grid (~5 m), but the drawn line stays your exact geometry — the grid only colours, never redraws.",
  'methode.rawStep1Title': "You upload",
  'methode.rawStep1Desc': "GPX, FIT or Strava export",
  'methode.rawStep2Title': "We mask",
  'methode.rawStep2Desc': "the ends of every trace",
  'methode.rawStep3Title': "We overlay",
  'methode.rawStep3Desc': "popularity reveals the corridors",
  'methode.rawLatticeDetails': "How popularity is computed",
  'methode.rawLatticeDetail': "Each masked point is binned into a cell of a density grid (~5 m across). The cell counts passes and distinct users — that count sets the intensity (the colour) of the trace. But the displayed line stays the real, precise GPS polyline: the grid is a counter, never a template.",
  'methode.communityGrows': "The more the community shares traces, the more gaps are filled.",
  'methode.privacyTitle': "Privacy: endpoint masking",
  'methode.privacyDesc': "Your traces appear precise, but the start and end of every activity are automatically trimmed (about 200 m) so your home or workplace is never revealed.",
  'methode.privacyMaskCallout': "This is endpoint masking, not K-anonymity: we cut the sensitive ends off each trace rather than merging traces together.",
  'methode.privacyFloorTitle': "The distinct-contributor threshold",
  'methode.privacyFloorDetail': "In addition, a per-cell distinct-user floor can hide sectors ridden by too few people. During the private beta it's set to 1 (everything shows, including a lone trace); it will be raised as the community grows.",
  'methode.privacyNotKanon': "The old per-matched-segment K-anonymity model is gone: the raw map replaces it with these two safeguards, plus a members-only export.",
  'methode.provenanceTitle': "Your traces, your consent",
  'methode.provenanceDesc': "Only the traces you upload yourself — your Strava export (.zip), or GPX/FIT files — feed the public community map, with your explicit consent and under the open ODbL licence.",
  'methode.licenseTitle': "Open for everyone, paid for closed platforms",
  'methode.licenseDesc': "The community map is published under ODbL 1.0: free reuse — commercial included — under two conditions: credit the contributors, and republish any derived database under the same open licence (share-alike). That is what keeps the commons a commons.",
  'methode.licenseDual': "Why the dual regime? So that industry giants can't use the community's data without an agreement. A closed platform that refuses share-alike (folding our data into a proprietary product without opening it back up) must acquire a separate commercial licence — contact: paul@epauler.fr. By contributing you grant the project that non-exclusive right; revenue funds the infrastructure. Your traces themselves stay free under ODbL, for everyone, forever.",
  'methode.provenanceApi': "Activities synced through the Strava API stay strictly personal (Strava API policy §5.4/§5.10): never on the public map, never in the open export.",
  'methode.integrityTitle': "Untouched traces",
  'methode.integrityDesc': "Your GPX file is stored exactly as you recorded it. We never re-route it, never simplify it, never snap it to a road. Only the two ends are masked for privacy — the rest is kept to the last point.",
  'methode.routingTitle': "Routing? We leave you the best tools",
  'methode.routingDesc': "Common Trails is an open popularity map and a source of traces. It's not a router — the ecosystem does that better than we could. Export any trace as GPX and open it in gpx.studio, BRouter or Komoot to build and follow your route.",
  'methode.routingNote': "The built-in router was removed: being a router isn't our job.",
  'methode.footerExplain': "Explain what we do, acknowledge what we don't know.",
  'methode.exploreMap': "Explore the heatmap →",
  'methode.footerLicense': "Source code AGPLv3 · Data ODbL 1.0 · Endpoints masked",

  // ── Privacy page ─────────────────────────────────────────────────────────────
  'privacy.lastUpdate': 'Last updated: 29 July 2026',
  'privacy.dataCollected': '2. Data collected',
  'privacy.activityMeta': 'Activity metadata: name, sport, distance, elevation, moving time',
  'privacy.techData': 'Technical data: IP address, browser type (server logs, kept 30 days)',
  'privacy.privateVsCommunity': '3. Private data vs. community data',
  'privacy.activitiesPrivate': 'Your activities are private. They are never shared, sold or made accessible to other users.',
  'privacy.kAnonymity': 'The community map is public and open. It is fed only by the traces you upload yourself (GPX/FIT files or a Strava export), with your explicit consent — never by activities synced via the Strava API. Your traces appear precise, but the start and end of each activity are automatically masked (200 m by default) so as not to reveal your sensitive locations (home, work). During the private beta a trace may be visible individually; a distinct-contributor threshold, tightened as the community grows, will progressively limit display to areas ridden by several users.',
  'privacy.odbl': 'The community map data is published as open data under the ODbL 1.0 licence (Open Database License): anyone may reuse and download it, provided the source is credited and the same open licence is kept (share-alike). In parallel, contributors (consent v5) grant the project a non-exclusive right to offer this data under a separate commercial licence to parties unwilling to honour share-alike — revenue funds the project.',
  'privacy.usedFor': 'Your data is used exclusively for:',
  'privacy.routeCalc': 'Let you export your traces as GPX to third-party tools (gpx.studio, BRouter, Komoot)',
  'privacy.noSponsored': 'No sponsored or promoted results on the map',
  'privacy.oauthExplain': 'When you connect a Strava or Garmin Connect account, we only access your activities via their official API, with your explicit consent (OAuth2).',
  'privacy.revokeAccess': 'You can revoke access at any time from your Strava or Garmin Connect account settings. Revocation stops all future synchronisation.',
  'privacy.noPassword': "We don't store your Strava or Garmin password — only an encrypted access token.",
  'privacy.hosting': '6. Hosting and security',
  'privacy.hostingGcp': 'Hosting: Google Cloud Platform (region europe-west1, Belgium)',
  'privacy.retention': 'Your data is retained as long as your account is active.',
  'privacy.deleteAccount': 'Request complete deletion of your account and all your data by contacting us by email',
  'privacy.gdpr': 'In accordance with the General Data Protection Regulation, you have the following rights:',
  'privacy.rightAccess': 'Access: consult your personal data',
  'privacy.rightPortability': 'Portability: export your data (GPX)',
  'privacy.policyUpdate': 'This policy may be updated. In case of significant changes, a notification will be displayed on the site. The last update date is indicated at the top of this page.',

  // ── Map — Account dropdown ───────────────────────────────────────────────────
  'map.layers': 'Layers',
  'map.account.loggedAs': 'Logged in as',
  'map.account.viewStats': 'View my stats',
  'map.account.myTraces': 'My traces',
  'map.account.importGarmin': 'Import from Garmin',
  'map.account.importKomoot': 'Import from Komoot',
  'map.account.komootDesc': 'Export your GPX → import here',
  'map.account.importContribute': 'Import / Contribute',
  'map.account.importContributeDesc': 'GPX, ZIP, Strava / Garmin / Komoot export',
  'map.account.useAsOverlay': 'Use the per-sport heatmap to plan routes',
  'map.account.useAsOverlayDesc': 'gpx.studio / VisuGPX overlay, per sport',
  'map.menuButton': 'Menu',
  'map.account.createOrLogin': 'Sign up or log in',
  'map.account.accountOf': "{name}'s account",
  'map.account.tracesImported': '{count} track{s} imported',
  'map.account.admin': 'Admin',
  'map.account.adminDesc': 'Dashboard monitoring',
  'map.account.logout': 'Log out',

  // ── Long-running import reassurance + post-login notifications ──────────────
  'strava.canCloseTab': 'You can close this tab — the import keeps running in the background.',
  'strava.notifyOnReturn': "You'll get a notification next time you log in.",
  // ── Import progress flow (stepper, ETA, phase titles) ──────────────────────
  'strava.phase.discovering': 'Discovery',
  'strava.phase.ingesting': 'Import',
  'strava.phase.gpsUpgrade': 'HD GPS',
  'strava.phase.photoImport': 'Photos',
  'strava.eta.fewSeconds': '~a few seconds',
  'strava.eta.remaining': 'left',
  'strava.page.label': 'Page',
  'strava.importingActivities': 'Importing {count} activities',
  'strava.canLeaveImport': 'You can leave — the import keeps running in the background',
  'strava.tooLong': 'Too long?',
  'strava.photoImportTitle': 'Importing photos',
  'strava.photosImported': '{count} photo(s) imported',
  'strava.searchingPhotos': 'Searching for photos…',
  'strava.failedAtPhase': 'Failed at phase',
  'strava.seeDetails': 'See details',
  'strava.gpsUpgradeInline': 'GPS upgrade',
  'strava.resync': 'Re-sync',
  'strava.tipFasterGpx': 'Faster import with GPX',
  'strava.tip': 'Tip',
  'strava.stravaEtaTip': 'With {count} activities, importing via Strava will take ~{min} min (Strava API limit).',
  'strava.zipEmailHint': 'Strava will email you a ZIP. Import it on',
  'strava.theMap': 'the map',
  'strava.withImportButton': 'using the Import button.',
  'strava.heatNote': ' — your tracks will appear on the heatmap in a few minutes',
  'strava.importingFiles': 'Importing {count} file(s)…',
  'strava.gpxAltTitle': 'Quick alternative: Strava GPX export',
  'strava.gpxStep1': 'Go to',
  'strava.gpxStep2': 'Click',
  'strava.requestDataBtn': 'Request your data',
  'strava.gpxStep2Note': '(does not delete your account)',
  'strava.gpxStep3': "Wait for Strava's email (a few hours)",
  'strava.gpxStep4': 'Import the ZIP above — instant!',
  // ── Strava import wizard — strings previously hardcoded in app/strava/page.tsx ──
  'strava.communityMap': 'Community map',
  'strava.contributeHeatmap': 'Contribute to the heatmap',
  'strava.canLeavePage': 'You can leave the page, the import keeps running in the background.',
  'strava.importProgress': 'Import {current}/{total}...',
  'strava.resultImported': '{count} imported',
  'strava.resultSkipped': '{count} skipped',
  'strava.resultFailed': '{count} error{s}',
  'strava.compatible': 'Compatible: Strava (.gpx), Garmin (.fit/.gpx), Komoot (.gpx), Wahoo (.fit)',
  'strava.chooseFiles': 'Choose files',
  'strava.blockArchiveTitle': 'My .zip archive (Strava or Garmin)',
  'strava.blockFilesTitle': 'My GPX / FIT traces',
  'strava.chooseArchive': 'Choose my .zip archive',
  'strava.archiveHelpLink': 'How to get my Strava archive (3 min)',
  'strava.sportFromArchive': 'The sport is read automatically from your archive.',
  'strava.sportConfirmTitle': 'These traces are:',
  'strava.importViaStrava': 'Import via Strava (slower)',
  'strava.queued': 'Queued',
  'strava.queuedOther': 'Another import is already running on your account.',
  'strava.queuedWait': 'Yours will start as soon as the previous one finishes. You can close this tab.',
  'strava.readingHistory': 'Reading Strava history...',
  'strava.statNew': 'new',
  'strava.statError': 'error{s}',
  'strava.statImported': 'imported',
  'strava.startingImport': 'Starting the import...',
  'strava.connectingReading': 'Connecting to Strava and reading the first pages.',
  'strava.continuesBackground': 'Continues in the background',
  'strava.gpsHighQuality': 'High-quality GPS ✓',
  'strava.importStravaMin': 'Strava import: ~{minutes} min',
  'strava.willExportGpx': "I'll export my GPX files",
  'strava.continueWithStrava': 'Continue with Strava',
  'notif.markRead': 'Mark as read',
  'notif.markAllRead': 'Mark all as read',
  'notif.activitySynced.title': 'Ride synced',
  'notif.activitySynced.body': 'Your ride « {name} » is synced to your personal view ✓',
  'notif.activitySynced.bodyNoName': 'Your ride is synced to your personal view ✓',

  // ── MAP i18n consolidation (fix/map-i18n-consolidation) ──────────────────────
  // Toolbar
  'map.toolbar.heatmapLayer': '🔥 Community heatmap',
  'map.offroadCombined': 'Off-road (MTB+Gravel)',
  'map.toolbar.timeAll': 'All',
  'map.toolbar.time1y': '1 yr',
  'map.toolbar.time60d': '60d',
  'map.toolbar.time30d': '30d',
  'map.dfciTracks': 'DFCI tracks',
  'map.dfciTracksCount': 'DFCI tracks ({count})',
  'map.toolbar.moreLayers': 'More layers',
  'map.toolbar.cellCoverage': 'Cell coverage',
  'map.toolbar.hillshade': 'Relief / Hillshade',
  'map.toolbar.stravaPhotos': 'Strava photos',
  'map.toolbar.waymarkedHeader': 'Waymarked trails',
  'map.toolbar.basemap': 'Basemap',
  'map.toolbar.exportTitle': 'Download the community heatmap (PMTiles / GPX / GeoJSON — for gpx.studio, VisuGPX · ODbL)',
  'map.toolbar.exportAria': 'Download the community heatmap',
  'map.toolbar.exportBtn': '⬇️ Export',
  'map.toolbar.exportSection': 'Export',
  'map.toolbar.exploreModeOn': '🔍 Explore mode',
  'map.toolbar.exploreModeOff': '🔍 Explore the heatmap',
  // Basemap picker labels + titles
  'map.basemap.label.bright': 'Light',
  'map.basemap.label.dark': 'Dark',
  'map.basemap.label.liberty': 'Cycling',
  'map.basemap.label.cyclosm': 'CyclOSM',
  'map.basemap.label.ign': 'IGN',
  'map.basemap.label.hybrid': 'Hybrid',
  'map.basemap.label.satellite': 'Satellite',
  'map.basemap.title.bright': 'Light basemap',
  'map.basemap.title.dark': 'Dark basemap (better heatmap contrast)',
  'map.basemap.title.liberty': 'Cycling basemap with tracks and paths',
  'map.basemap.title.ign': 'IGN Plan v2 — French topo',
  'map.basemap.title.hybrid': 'Satellite + semi-transparent IGN',
  'map.basemap.title.satellite': 'Esri satellite imagery',
  'map.waymarked.hiking': 'Hiking (GR/PR)',
  'map.waymarked.cycling': 'Cycling (EuroVelo)',
  'map.waymarked.mtb': 'MTB',
  // Import modals (Garmin / Komoot / RoutingInfo / Lightbox)
  'map.howRouting.signalsIntro': 'To do this, we combine several signals:',
  'map.howRouting.profileNote': 'The chosen profile (road, gravel, MTB…) influences these priorities.',
  'map.howRouting.learnMore': 'Learn more →',
  'map.lightbox.photo': 'Photo',
  // Map page — default names
  'map.activityDefaultName': 'Activity',
  // Map page — import flow (single-file / ZIP)
  // Map page — warming messages + overlays / popups
  'map.loadingCommunityTraces': 'Loading community traces',
  'map.edgesSuffix': ' ({count} edges)',
  'map.betaImportLink': 'Import my traces',
  'map.activitiesLoadError': 'Could not load your routes. Retrying may help.',
  'map.loadingHeatmap': 'Loading heatmap...',
  'map.gpxDropHint': 'Drop a .gpx file to preview it',
  'map.closePreview': 'Close preview',
  'map.import': 'Import',
  'map.importing': 'Importing…',
  'map.imported': 'Imported',
  'map.importError': 'Import failed — please retry.',
  'map.addAnnotation': 'Add annotation',
  'map.annotationNotePlaceholder': 'Note (optional)',
  'map.segmentHeatmap': 'Heatmap segment',
  'map.contributors': '{count} contributor{s}',
  'map.passes': '{count} pass{es}',
  'map.directionOfTravel': 'Direction of travel',
  'map.traceFromHere': 'Trace from here',
  'map.sportFilter.all': 'All',
  'map.sportFilter.running': 'Running',
  'map.comparison': 'Comparison',
  'map.openOnPanoramax': 'Open in Panoramax →',
  'map.linkCopied': 'Link copied!',
  // Legend
  'map.legend.popularity': 'Popularity',
  'map.legend.offroad': 'Off-road (MTB + Gravel)',
  // Selected activity card
  'map.activity.distance': 'Distance',
  'map.activity.elevGain': 'Elev.',
  'map.activity.date': 'Date',
  'map.activity.viewDetails': 'View details',
  'map.activity.similar': 'Similar',
  // Activity photo strip
  'map.photos.stripLabel': 'Photos ({count})',
  'map.photos.stravaPhoto': 'Strava photo',
  'map.photos.altFallback': 'Photo',

  // ── Overlay / heatmap in gpx.studio ───────────────────────────────────────
  'calque.breadcrumb': 'Heatmap as an overlay',
  'calque.title': 'The per-sport heatmap as a gpx.studio overlay',
  'calque.intro': "Overlay ONE sport's community heatmap (road, gravel, MTB, off-road, running) on your favourite planner as a transparent layer. Pick the sport, copy the URL: three settings, thirty seconds.",
  'calque.urlLabel': 'The overlay address',
  'calque.copy': 'Copy',
  'calque.copied': 'Copied ✓',
  'calque.stepsTitle': 'Step by step',
  'calque.step1Title': 'Open the map settings',
  // Menu path validated in the real gpx.studio (2026-09-05); the map-icon
  // hover is the alternative route to the same panel.
  'calque.step1Text': "On gpx.studio, open the “Settings” menu → “Map Layers”. (Or: hover the map icon at the top right and click the cog — same panel.)",
  'calque.step2Title': 'Add a custom layer',
  'calque.step2Text': "Under “Custom layers”, choose “Add custom layer”, then fill in exactly these four fields:",
  'calque.step3Title': 'Save',
  'calque.step3Text': "Click “Add layer”, then “Save”. The layer is remembered in your browser for next time.",
  'calque.step4Title': 'Turn the overlay on',
  'calque.step4Text': "Hover the map icon again, go to “Overlays › Custom” and tick Chemins Communs.",
  'calque.step4Text2': "The community's traces appear in purple → orange on top of your basemap. The more a segment is ridden, the warmer and brighter it glows.",
  'calque.panelTitle': 'New custom layer',
  'calque.fieldName': 'Name',
  'calque.fieldUrl': 'Tiles URL',
  'calque.fieldZoom': 'Max zoom',
  'calque.fieldType': 'Type',
  'calque.valType': 'Overlay',
  'calque.trap1Title': 'The right URL is the .png one',
  'calque.trap1Text': "Paste the .png template. Do not paste the .json address (gpx.studio would treat it as vector and show nothing), nor the .pmtiles file.",
  'calque.trap2Title': 'Pick “Overlay”, not “Basemap”',
  'calque.trap2Text': "Our tiles are transparent — just the traces, on nothing. As a basemap you'd see the traces floating on a grey screen; as an overlay they sit on top of your usual map.",
  'calque.whyTitle': 'Why an overlay?',
  'calque.whyText': "The heatmap isn't a full map: it's a layer of traces meant to sit on top of your preferred basemap (OpenStreetMap, satellite, IGN…). Same idea as Strava's “Heatmap” overlay.",
  'calque.noteZoomT': 'Zoom',
  'calque.noteZoom': "Data up to zoom level 14. Above that, gpx.studio upscales the last tile — that's expected.",
  'calque.noteCoverageT': 'Coverage',
  'calque.noteCoverage': 'Mainland France. The overlay grows denser as the community contributes its traces.',
  'calque.noteAppsT': 'Other apps',
  'calque.noteApps': 'The same URL works in any app that accepts a raster XYZ layer (VisuGPX, MapLibre, Leaflet…).',
  'calque.noteLicenseT': 'Licence',
  'calque.noteLicense': 'Data © Chemins Communs contributors, reusable with attribution and share-alike —',
};

export default en;
